import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Input, notification, Spin } from 'antd';
import { AppstoreAddOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useUserInfo } from 'hooks';
import { isArray } from 'lodash';

import Messages from 'i18n/messages/home';
import messagesProduct from 'i18n/messages/product';

import {
  Form,
  StyledModal,
  ButtonSmallIcon,
  ContainerButton,
  ItemButton,
} from 'common/components';

import { addSearchProductsToApl } from 'services/apl';
import { ROUTE } from 'static/Constants';
import { useGetQuery } from 'hooks/useQuery';

import './AddProductToNewApl.less';
import { handleCreateSearchContainer } from 'pages/reporting/utils';

const AddSearchProductToNewApl = ({
  disabledFilterSearch,
  searchCategoriesEffected,
  searchText,
  filterProducts,
  queryConditions,
  gridFilters,
}) => {
  const [visibleModal, setVisibleModal] = useState(false);

  return (
    <>
      <ContainerButton>
        <ItemButton>
          <ButtonSmallIcon
            icon={<AppstoreAddOutlined style={{ height: 26 }} />}
            label={Messages.addSearchProductToNewApl}
            onClick={() => setVisibleModal(true)}
            className='button-item-dropdown'
            disabled={disabledFilterSearch}
          />
        </ItemButton>
      </ContainerButton>

      {visibleModal && (
        <ModalFormCreateApl
          visibleModal={visibleModal}
          turnOffModal={() => setVisibleModal(false)}
          searchCategoriesEffected={searchCategoriesEffected}
          searchText={searchText}
          filterProducts={filterProducts}
          queryConditions={queryConditions}
          gridFilters={gridFilters}
        />
      )}
    </>
  );
};

const layoutForm = {
  labelCol: { span: 6 },
  wrapperCol: { span: 17 },
};
const ModalFormCreateApl = ({
  visibleModal,
  turnOffModal,
  searchCategoriesEffected,
  searchText,
  filterProducts,
  queryConditions,
  gridFilters,
}) => {
  const [formInstance] = Form.useForm();
  const intl = useIntl();
  const { userInfo } = useUserInfo();

  const [loading, setLoading] = useState(false);

  const { pathname } = useLocation();
  const query = useGetQuery();
  const memberId = query.get('memberId');

  const isMemberProductView = ROUTE.MEMBER_PRODUCTS === pathname;

  const categoryParams = window?.productCategory;
  const brickCodeParams = window?.brickCodeHierarchy;

  const overlayFilter = gridFilters?.find(
    (item) => item === 'withoutoverlay' || item === 'withoverlay'
  );

  const isOnlyChildMembersFilter = gridFilters?.includes(
    'childCompanyProducts'
  );

  const handleSubmitForm = () => {
    formInstance.validateFields().then(() => {
      setLoading(true);
      let params = formInstance.getFieldsValue();
      params.search = searchText;
      let filters = [];
      let arrValue = [];

      //* Add filter by member for member product view
      if (isMemberProductView && memberId) {
        filters.push({
          fieldName: 'ownerId',
          value: parseInt(memberId),
          filterType: 'Equal',
        });
      }

      if (filterProducts.indexOf('subscription') > -1) {
        filters.push({
          fieldName: 'subscription',
          value: true,
          filterType: 'Equal',
        });
      }
      if (filterProducts.indexOf('syndication') > -1) {
        filters.push({
          fieldName: 'syndication',
          value: true,
          filterType: 'Equal',
        });
      }
      if (filterProducts.indexOf('owner') > -1) {
        filters.push({
          fieldName: 'isOwner',
          value: true,
          filterType: 'Equal',
        });
      }
      if (filterProducts.indexOf('pending') > -1) {
        filters.push({
          fieldName: 'status',
          value: 'Pending',
          filterType: 'Equal',
        });
      }

      if (filterProducts.indexOf('shared') > -1) {
        filters.push({
          fieldName: 'ownerId',
          value: userInfo?.member?.id,
          filterType: 'NotEqual',
        });
      }

      if (filterProducts.indexOf('active') > -1) {
        filters.push({
          fieldName: 'status',
          value: 'Active',
          filterType: 'Equal',
        });
      }

      if (filterProducts.indexOf('productCategory') > -1) {
        filters.push(...categoryParams);
      }
      if (filterProducts.indexOf('brickCodeHierarchy') > -1) {
        filters.push(...brickCodeParams);
      }

      if (filterProducts.indexOf('unmatched') > -1) {
        filters.push({
          fieldName: 'meetMinRequirement',
          value: false,
          filterType: 'Equal',
        });
        filters.push({
          fieldName: 'status',
          value: 'Active',
          filterType: 'Equal',
        });
      }

      if (filterProducts.indexOf('privateLabel') > -1) {
        filters.push({
          fieldName: 'isPrivateLabel',
          value: true,
          filterType: 'Equal',
        });
      }

      if (
        filterProducts.indexOf('Pallet') > -1 ||
        filterProducts.indexOf('Master Case') > -1 ||
        filterProducts.indexOf('Case') > -1 ||
        filterProducts.indexOf('Inner Pack') > -1 ||
        filterProducts.indexOf('Unit') > -1
      ) {
        if (filterProducts.indexOf('Pallet') > -1) {
          arrValue.push('Pallet');
        }
        if (filterProducts.indexOf('Master Case') > -1) {
          arrValue.push('Master Case');
        }
        if (filterProducts.indexOf('Case') > -1) {
          arrValue.push('Case');
        }
        if (filterProducts.indexOf('Inner Pack') > -1) {
          arrValue.push('Inner Pack');
        }
        if (filterProducts.indexOf('Unit') > -1) {
          arrValue.push('Unit');
        }
      }

      const aplFilter =
        isArray(filterProducts) &&
        gridFilters.find((val) => val?.filter === 'apl')?.value?.id;

      params.filters = filters;

      const advancedSearch = handleCreateSearchContainer(queryConditions);

      params.advancedSearchContainer = advancedSearch;
      params.packageLevels = arrValue;
      params.aplId = aplFilter;
      params.searchCategory = searchCategoriesEffected;
      params.overlayFilter = overlayFilter ?? undefined;
      params.isOnlyChildMembers = isOnlyChildMembersFilter ?? undefined;

      addSearchProductsToApl(params)
        .then((resp) => {
          if (resp.isSuccess) {
            const message =
              resp?.message ||
              intl.formatMessage(messagesProduct.addSearchProductToAplSuccess);
            notification.success({
              message,
            });

            turnOffModal();
          } else {
            notification.error({
              message: resp?.message,
            });
          }
        })
        .catch((error) => {
          notification.error({
            message: error,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  const modalProps = {
    title: intl.formatMessage(messagesProduct.addSearchProductToAplLabel),
    accept: '.xlsx, .xls, .csv',
    destroyOnClose: true,
    visible: visibleModal,
    onCancel: turnOffModal,
    onOk: handleSubmitForm,
    okButtonProps: { loading: loading },
    cancelButtonProps: { disabled: loading },
    maskClosable: false,
    closable: !loading,
  };

  return (
    <StyledModal {...modalProps}>
      <Spin
        spinning={loading}
        tip='Add search results to new APL...'
        size='large'
      >
        <Form
          {...layoutForm}
          form={formInstance}
          name='formAddSearchProductToNewApl'
        >
          <Form.Item
            name='aplName'
            label='Name:'
            colon={false}
            rules={[{ required: true, message: 'Please input APL name' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name='aplDescription' label='Description:' colon={false}>
            <Input />
          </Form.Item>
        </Form>
      </Spin>
    </StyledModal>
  );
};

export default AddSearchProductToNewApl;
